import { computed, nextTick, ref } from "vue";
import { useEmojiPickerRecentHistory } from "@/src/design_system/button_and_controls/emoji_picker/composables/useEmojiPickerRecentHistory";
import { EMOJI_PICKER_BATCH_SIZE } from "@/src/design_system/button_and_controls/emoji_picker/constants/pagination";
import type { EmojiPickerToneOption } from "@/src/design_system/button_and_controls/emoji_picker/types/panel";
import {
  buildEmojiSections,
  limitEmojiSections,
} from "@/src/design_system/button_and_controls/emoji_picker/utils/emojiSections";
import {
  EMOJI_CATEGORY,
  EMOJI_SKIN_TONES,
  EMOJI_SKIN_TONE,
  EMOJI_SKIN_TONE_SAMPLE_UNICODE,
} from "@/src/shared/emoji/constants";
import type { EmojiCategory, EmojiDisplayItem, EmojiSkinTone } from "@/src/shared/emoji/types";
import { applyEmojiSkinTone, findEmojiByUnicode } from "@/src/shared/emoji/utils";

export const useEmojiPickerPanel = () => {
  const search = ref("");
  const category = ref<EmojiCategory>(EMOJI_CATEGORY.All);
  const skinTone = ref<EmojiSkinTone>(EMOJI_SKIN_TONE.Neutral);
  const hoveredEmoji = ref<EmojiDisplayItem | null>(null);
  const visibleEmojiCount = ref(EMOJI_PICKER_BATCH_SIZE);
  const isLoadMorePending = ref(false);
  const { recentEmojis, refreshRecentEmojis, rememberEmoji } = useEmojiPickerRecentHistory();

  const emojiSections = computed(() => {
    return buildEmojiSections({
      category: category.value,
      recentEmojis: recentEmojis.value,
      search: search.value,
      skinTone: skinTone.value,
    });
  });

  const totalEmojiCount = computed(() => {
    return emojiSections.value.reduce((total, section) => total + section.emojis.length, 0);
  });

  const visibleSections = computed(() => limitEmojiSections(emojiSections.value, visibleEmojiCount.value));

  const renderedEmojiCount = computed(() => {
    return visibleSections.value.reduce((total, section) => total + section.emojis.length, 0);
  });

  const hasMoreEmojis = computed(() => renderedEmojiCount.value < totalEmojiCount.value);
  const previewEmoji = computed(() => {
    const preview = hoveredEmoji.value ?? emojiSections.value[0]?.emojis[0];
    return preview ? applyEmojiSkinTone(preview, skinTone.value) : null;
  });

  const toneOptions = computed<EmojiPickerToneOption[]>(() => {
    const toneSample = findEmojiByUnicode(EMOJI_SKIN_TONE_SAMPLE_UNICODE);
    if (!toneSample) return [];

    return EMOJI_SKIN_TONES.map(tone => ({
      tone,
      native: applyEmojiSkinTone(toneSample, tone).native,
      label: window.I18n.designSystem.emojiPicker.skinTones[tone],
    }));
  });

  const resetVisibleEmojis = (): void => {
    isLoadMorePending.value = false;
    visibleEmojiCount.value = EMOJI_PICKER_BATCH_SIZE;
  };

  const resetPickerState = (): void => {
    search.value = "";
    category.value = EMOJI_CATEGORY.All;
    skinTone.value = EMOJI_SKIN_TONE.Neutral;
    hoveredEmoji.value = null;
    resetVisibleEmojis();
  };

  const selectCategory = (selectedCategory: EmojiCategory): void => {
    search.value = "";
    category.value = selectedCategory;
    hoveredEmoji.value = null;
    resetVisibleEmojis();
  };

  const updateSearch = (value: string | number): void => {
    search.value = String(value);
    if (search.value.trim()) category.value = EMOJI_CATEGORY.All;
    hoveredEmoji.value = null;
    resetVisibleEmojis();
  };

  const loadMoreEmojis = (): void => {
    if (!hasMoreEmojis.value || isLoadMorePending.value) return;

    isLoadMorePending.value = true;
    visibleEmojiCount.value = Math.min(visibleEmojiCount.value + EMOJI_PICKER_BATCH_SIZE, totalEmojiCount.value);

    nextTick(() => {
      isLoadMorePending.value = false;
    });
  };

  const selectSkinTone = (tone: EmojiSkinTone): void => {
    skinTone.value = tone;
  };

  const setHoveredEmoji = (emoji: EmojiDisplayItem): void => {
    hoveredEmoji.value = emoji;
  };

  return {
    category,
    hasMoreEmojis,
    loadMoreEmojis,
    previewEmoji,
    refreshRecentEmojis,
    rememberEmoji,
    resetPickerState,
    search,
    selectCategory,
    selectSkinTone,
    setHoveredEmoji,
    skinTone,
    toneOptions,
    updateSearch,
    visibleSections,
  };
};
